import type { CabinetRoleEditorView } from "@planerius/contracts/cabinet-auth"
import { DANGER_PERMISSIONS, PERMISSION_LABELS } from "@planerius/contracts/cabinet-roles"
import type { ReactNode } from "react"
import { useEffect, useState } from "react"

import { copyRole, deleteRole, listRoleEditor, updateRole } from "../../../api/roles.api"
import { isApiProblem } from "../../../api/problem"
import { JournalDialog } from "../../../journal/appointment-dialogs"
import { useIsPhone } from "../../../journal/use-viewport"
import { ProblemScreen } from "../../../shell/states"
import { Button, Checkbox, Chip, Chips, Skeleton, TextField } from "../../../ui"
import { StandHeader } from "../../directories/stand"
import { useResource } from "../../directories/use-resource"
import {
  type RoleForm,
  deleteDenial,
  formOf,
  isDirty,
  peopleWords,
  roleOrigin,
  sectionsOf,
  toggleGroup,
  togglePermission,
} from "./roles-model"
import styles from "./roles-screen.module.css"

/**
 * 4-Б11 «Роли и права» — /settings/roles (макет П2, ТЗ 04, Б11). Открывает владелец, редко.
 *
 * Сверху — роли чипами со счётчиком людей; ниже — имя роли, «только свои записи» и
 * «второй фактор»; семь прав про деньги и данные клиентов — отдельно и заметно; затем
 * разделы галочкой на группу. Две подписи из ТЗ стоят на экране, а не в справке: про то,
 * чего здесь нет (видимость телефона и денег мастеру — 4-Б12), и про мастера.
 *
 * НА ТЕЛЕФОНЕ — ТОЛЬКО ПРОСМОТР (ТЗ Б11): матрица из сорока прав пальцем не настраивается,
 * а случайное касание выдаёт мастеру выгрузку базы.
 */
export function RolesScreen(props: { readonly onOpenVisibility: () => void }): ReactNode {
  const isPhone = useIsPhone()
  const roles = useResource((signal) => listRoleEditor(signal), "roles-editor")
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [form, setForm] = useState<RoleForm | null>(null)
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set())
  const [busy, setBusy] = useState(false)
  const [outcome, setOutcome] = useState<{ readonly tone: "ok" | "error"; readonly text: string } | null>(null)
  const [copying, setCopying] = useState(false)

  const items = roles.data?.items ?? []
  const role = items.find((item) => item.id === selectedId) ?? items.find((item) => item.code === "admin") ?? items[0] ?? null

  // Форма — заново при смене роли; перечитывание списка после сохранения её не сбрасывает,
  // если человек остался на той же роли (сохранённое и так в форме).
  useEffect(() => {
    if (role !== null && (form === null || selectedId !== role.id)) {
      setSelectedId(role.id)
      setForm(formOf(role))
      setExpanded(new Set())
    }
  }, [role, form, selectedId])

  if (roles.problem !== null && roles.data === null) {
    return <ProblemScreen problem={roles.problem} onRetry={roles.reload} />
  }
  if (role === null || form === null) {
    return (
      <div className={styles.screen}>
        <StandHeader title="Роли и права" />
        <div className={styles.card}>
          <Skeleton width="100%" height={420} radius={12} />
        </div>
      </div>
    )
  }

  const readOnly = isPhone
  const permissionsLocked = readOnly || role.locked
  const dirty = isDirty(role, form)
  const update = (patch: Partial<RoleForm>): void => {
    setForm({ ...form, ...patch })
    setOutcome(null)
  }
  const select = (next: CabinetRoleEditorView): void => {
    setSelectedId(next.id)
    setForm(formOf(next))
    setExpanded(new Set())
    setOutcome(null)
  }

  const save = async (): Promise<void> => {
    setBusy(true)
    setOutcome(null)
    try {
      const saved = await updateRole({
        roleId: role.id,
        name: form.name.trim(),
        permissions: [...form.permissions] as never,
        restrictToOwnRecords: form.restrictToOwnRecords,
        requireMfa: form.requireMfa,
        expectedUpdatedAt: role.updatedAt,
      })
      setForm(formOf(saved))
      setOutcome({ tone: "ok", text: `Роль «${saved.name}» сохранена. Права действуют с ближайшего запроса сотрудника.` })
      roles.reload()
    } catch (error) {
      if (!isApiProblem(error)) throw error
      setOutcome({ tone: "error", text: error.detail ?? error.title })
    } finally {
      setBusy(false)
    }
  }

  const remove = async (): Promise<void> => {
    setBusy(true)
    setOutcome(null)
    try {
      await deleteRole({ roleId: role.id, expectedUpdatedAt: role.updatedAt })
      setSelectedId(null)
      setForm(null)
      roles.reload()
    } catch (error) {
      if (!isApiProblem(error)) throw error
      setOutcome({ tone: "error", text: error.detail ?? error.title })
    } finally {
      setBusy(false)
    }
  }

  const deleteReason = deleteDenial(role)

  return (
    <div className={styles.screen}>
      <StandHeader title="Роли и права" />
      <div className={styles.card}>
        <div className={styles.head}>
          <h3 className={styles.headTitle}>Роли</h3>
          <span className={styles.hint}>
            Системные нельзя удалить, можно переименовать. Запрещено всё, что не разрешено явно.
          </span>
          <span className={styles.spacer} />
          <Button
            size="sm"
            deniedReason={readOnly ? "Роли настраиваются с компьютера" : undefined}
            onClick={() => setCopying(true)}
          >
            Создать роль — копией
          </Button>
        </div>

        <Chips label="Роли салона" className={styles.roles}>
          {items.map((item) => (
            <Chip
              key={item.id}
              active={item.id === role.id}
              count={item.membersCount}
              countLabel="человек на роли"
              onClick={() => select(item)}
            >
              {item.name}
            </Chip>
          ))}
        </Chips>

        <div className={styles.nameRow}>
          <TextField
            label="Название роли"
            className={styles.nameField}
            value={form.name}
            disabled={readOnly}
            maxLength={60}
            onChange={(event) => update({ name: event.target.value })}
          />
          <span className={styles.hint}>{roleOrigin(role)}</span>
          <span className={styles.spacer} />
          <Checkbox
            checked={form.restrictToOwnRecords}
            disabled={permissionsLocked}
            onChange={(checked) => update({ restrictToOwnRecords: checked })}
          >
            Только свои записи
          </Checkbox>
          <Checkbox checked={form.requireMfa} disabled={readOnly} onChange={(checked) => update({ requireMfa: checked })}>
            Второй фактор
          </Checkbox>
        </div>

        <div className={styles.body}>
          {role.locked ? (
            <div className={styles.lockedNote}>
              Права роли владельца не меняются: сняв их, владелец запер бы салон снаружи. Роль можно переименовать
              и требовать для неё второй фактор.
            </div>
          ) : null}

          <div className={styles.dangerTitle}>Про деньги и данные клиентов — отдельно и заметно</div>
          {DANGER_PERMISSIONS.map((item) => (
            <Checkbox
              key={item.permission}
              className={styles.dangerRow}
              checked={form.permissions.has(item.permission)}
              disabled={permissionsLocked}
              onChange={() => update({ permissions: togglePermission(form.permissions, item.permission) })}
            >
              <span className={styles.dangerText}>
                <b>{item.title}</b>
                <span className={styles.dangerNote}> — {item.note}</span>
              </span>
            </Checkbox>
          ))}

          <div className={styles.sectionsTitle}>Разделы</div>
          <div className={styles.sections}>
            {sectionsOf(form.permissions).map((section) => (
              <div key={section.id} className={styles.section}>
                <div className={styles.sectionRow}>
                  <Checkbox
                    checked={section.state === "all"}
                    disabled={permissionsLocked}
                    onChange={() => update({ permissions: toggleGroup(form.permissions, section.permissions) })}
                  >
                    {section.title}
                  </Checkbox>
                  {section.permissions.length > 1 ? (
                    <button
                      type="button"
                      className={styles.more}
                      aria-expanded={expanded.has(section.id)}
                      onClick={() => {
                        const next = new Set(expanded)
                        if (next.has(section.id)) next.delete(section.id)
                        else next.add(section.id)
                        setExpanded(next)
                      }}
                    >
                      {section.state === "some" ? `частично: ${section.held} из ${section.permissions.length}` : "подробно"}
                    </button>
                  ) : null}
                </div>
                {expanded.has(section.id) ? (
                  <div className={styles.atoms}>
                    {section.permissions.map((permission) => (
                      <Checkbox
                        key={permission}
                        checked={form.permissions.has(permission)}
                        disabled={permissionsLocked}
                        onChange={() => update({ permissions: togglePermission(form.permissions, permission) })}
                      >
                        {PERMISSION_LABELS[permission]}
                      </Checkbox>
                    ))}
                  </div>
                ) : null}
              </div>
            ))}
          </div>

          <div className={styles.notHere}>
            <b>Чего здесь нет намеренно:</b> видимости телефона клиента и денег для мастера. Это не права роли, а
            переключатели салона —{" "}
            <button type="button" className={styles.link} onClick={props.onOpenVisibility}>
              «Что мастер видит о клиенте»
            </button>
            . Право отвечает «что положено по должности», настройка — «насколько салон доверяет коллективу».
          </div>
          <div className={styles.masterNote}>
            Мастеру доступны только его записи. Он может создавать и переносить свои записи — базу клиентов это ему
            не открывает: он ищет по точному номеру и видит одну карточку, а списка у него нет. Выгрузка базы — главный
            способ увести базу, поэтому по умолчанию закрыта всем, кроме владельца.
          </div>
        </div>

        {outcome === null ? null : (
          <div className={outcome.tone === "ok" ? styles.outcome : styles.problem} role={outcome.tone === "ok" ? "status" : "alert"}>
            {outcome.text}
          </div>
        )}

        <div className={styles.foot}>
          <Button
            size="sm"
            variant="primary"
            loading={busy}
            deniedReason={readOnly ? "Роли настраиваются с компьютера" : !dirty ? "Правок нет" : undefined}
            onClick={() => void save()}
          >
            Сохранить роль
          </Button>
          {role.isSystem ? null : (
            <Button
              size="sm"
              variant="danger-ghost"
              deniedReason={readOnly ? "Роли настраиваются с компьютера" : (deleteReason ?? undefined)}
              onClick={() => void remove()}
            >
              Удалить роль
            </Button>
          )}
          <span className={styles.hint}>
            {role.membersCount > 0
              ? `На роли «${role.name}» ${peopleWords(role.membersCount)}. Удалить роль, на которой есть люди, нельзя — сначала перевести их.`
              : "Удалить роль, на которой есть люди, нельзя — сначала перевести их."}
          </span>
        </div>
      </div>

      {copying ? (
        <CopyRoleDialog
          source={role}
          onClose={() => setCopying(false)}
          onCopied={(created) => {
            setCopying(false)
            roles.reload()
            select(created)
          }}
        />
      ) : null}
    </div>
  )
}

function CopyRoleDialog(props: {
  readonly source: CabinetRoleEditorView
  readonly onClose: () => void
  readonly onCopied: (role: CabinetRoleEditorView) => void
}): ReactNode {
  const [name, setName] = useState(`${props.source.name} — копия`)
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)

  const submit = async (): Promise<void> => {
    if (name.trim().length < 2) {
      setProblem("Назовите роль")
      return
    }
    setBusy(true)
    setProblem(null)
    try {
      props.onCopied(await copyRole({ sourceRoleId: props.source.id, name: name.trim() }))
    } catch (error) {
      if (!isApiProblem(error)) throw error
      setProblem(error.detail ?? error.title)
    } finally {
      setBusy(false)
    }
  }

  return (
    <JournalDialog title="Создать роль — копией" onClose={props.onClose}>
      <div className={styles.dialogBody}>
        <p className={styles.hint}>
          Новая роль получит права и флажки роли «{props.source.name}». Дальше её можно поправить и назначить людям.
        </p>
        <TextField
          label="Название новой роли"
          value={name}
          maxLength={60}
          autoFocus
          error={problem ?? undefined}
          onChange={(event) => {
            setName(event.target.value)
            setProblem(null)
          }}
        />
        <div className={styles.dialogButtons}>
          <Button onClick={props.onClose}>Отмена</Button>
          <Button variant="primary" loading={busy} onClick={() => void submit()}>
            Создать
          </Button>
        </div>
      </div>
    </JournalDialog>
  )
}
